<template>
  <div>
    <section class="relative w-full">
      <img
        v-if="data?.entry?.heroFsImage?.length"
        :src="data.entry.heroFsImage[0].url"
        :srcset="data.entry.heroFsImage[0].srcset"
        sizes="(max-width: 600px) 100px, (max-width: 1024px) 500px, 100vw"
        :alt="data.entry?.title"
        width="1920"
        height="1250"
        class="absolute inset-0 w-full h-full object-cover"
      >
      <div
        v-if="data?.entry?.heading"
        class="relative py-150 lg:py-[250px] w-full z-10"
      >
        <div class="container text-center">
          <div class="max-w-screen-lg mx-auto">
            <h1
              class="wysiwyg-title text-5xl md:text-6xl lg:text-7xl text-white"
              v-html="data.entry.heading"
            />
          </div>
        </div>
      </div>
      <div class="container">
        <ul class="flex flex-col lg:flex-row relative z-10 lg:space-x-20 pb-100">
          <li
            v-for="(product, productIndex) in data?.entry?.inspirationFeaturedProducts"
            :key="`product-${product.id}`"
            class="flex-1"
          >
            <NuxtLink
              :to="`/${product.uri}`"
              class="flex group mt-10"
              @click="$gtm.trackEvent({ ecommerce: null }); $gtm.trackEvent({
                event: 'select_item',
                ecommerce: {
                  items: [item({
                    product,
                    index: productIndex,
                    data: {
                      item_list_id: 'rooms_entry_hero_products',
                      item_list_name: 'Rooms entry hero products',
                    },
                  })],
                },
              })"
            >
              <div class="bg-primary-100 w-[130px]">
                <img
                  v-if="product.productImages?.length"
                  :src="product.productImages[0].url"
                  class="mix-blend-darken"
                  loading="lazy"
                  :alt="product.title"
                >
              </div>
              <div class="flex flex-1 p-20 ml-1 bg-primary-100 text-base items-center group-hover:underline transition">
                <p>
                  <b>{{ product.title }}</b><br>
                  <product-price :product="product" />
                </p>
              </div>
            </NuxtLink>
          </li>
        </ul>
      </div>
    </section>

    <section class="mt-50 mb-100">
      <div class="container">
        <div class="max-w-screen-xl mx-auto">
          <breadcrumb :breadcrumbs="[data?.overview, data?.entry].filter((b) => b)" />

          <div class="grid md:grid-cols-2 mt-100">
            <h2
              v-if="data?.entry?.heading2"
              class="wysiwyg-title text-5xl md:text-6xl xl:text-7xl text-primary-500"
              v-html="data.entry.heading2"
            />
            <div class="md:row-span-3">
              <img
                v-if="data?.entry?.image2?.length"
                :src="data.entry.image2[0].url"
                :srcset="data.entry.image2[0].srcset"
                sizes="(max-width: 600px) 345px, (max-width: 1024px) 497px, 100vw"
                :alt="data.entry.image2[0].title"
                width="1280"
                height="1330"
                class="md:mt-100 w-full"
                loading="lazy"
              >
            </div>
            <div
              v-if="data?.entry?.text"
              class="wysiwyg mt-25 md:pr-50 lg:pr-100 text-lg"
              v-html="data.entry.text"
            />
          </div>
        </div>

        <h3
          v-if="data?.entry?.heading3"
          class="wysiwyg-title text-3xl md:text-4xl mt-100 text-secondary"
          v-html="data.entry.heading3"
        />

        <div class="flex flex-wrap -mx-10 lg:-mx-15">
          <div
            v-for="product in data?.entry?.products"
            :key="`product-${product.id}`"
            class="w-1/2 md:w-1/3 px-10 lg:px-15 mt-50"
          >
            <product :product="product" />
          </div>
        </div>
      </div>
    </section>

    <content-blocks :content-blocks="data?.entry?.contentBlocks" />
  </div>
</template>

<script setup>
import {
  useI18n,
  useAsyncGql,
  useSeo,
  watch,
  useRoute,
  definePageMeta,
  onMounted,
  useDataLayer,
  useNuxtApp,
} from '#imports';

// Data
const { locale } = useI18n({ useScope: 'global' });
const route = useRoute();
const { data } = await useAsyncGql('inspiration', {
  site: locale.value,
  uri: route.path.substring(1).replace(/\/+$/, ''),
});
const { item } = useDataLayer();
const { $gtm } = useNuxtApp();

// Watcher
watch(data, (value) => {
  if (value?.entry?.seomatic) {
    useSeo(value.entry.seomatic);
  }
}, { immediate: true });

// Meta
definePageMeta({
  headerImage: true,
});

// Hooks
onMounted(() => {
  $gtm.trackEvent({ ecommerce: null });
  $gtm.trackEvent({
    event: 'view_item_list',
    ecommerce: {
      items: data.value?.entry?.inspirationFeaturedProducts.map((product, index) => item({
        product,
        index,
        data: {
          item_list_id: 'rooms_entry_hero_products',
          item_list_name: 'Rooms entry hero products',
        },
      })),
    },
  });
  $gtm.trackEvent({
    event: 'view_item_list',
    ecommerce: {
      items: data.value?.entry?.products.map((product, index) => item({
        product,
        index,
        data: {
          item_list_id: 'rooms_entry_products',
          item_list_name: 'Rooms entry products',
        },
      })),
    },
  });
});
</script>
